import { kendamaMapPoints as defaultKendamaMapPoints } from './data/kendamaMapPoints';
import { KendamaMapPoint, KendamaMapPointGroup, KendamaMapPointLogo } from './types';
import { isLocalBackend, phpApiUrl, phpAuthHeaders } from './apiEnv';

const KENDAMA_MAP_LOCAL_STORAGE_KEY = 'kendama-map-points';

const getMapFetchEndpoint = () =>
  isLocalBackend() ? '/api/kendama-map-points' : phpApiUrl('get_kendama_map_points');

const getMapSaveEndpoint = () =>
  isLocalBackend() ? '/api/admin/kendama-map-points' : phpApiUrl('admin_save_kendama_map_points');

const getMapSuggestionSubmitEndpoint = () =>
  isLocalBackend() ? '/api/user/kendama-map-suggestions' : phpApiUrl('user_submit_kendama_map_suggestion');

const normalizeMapPointGroup = (group: unknown): KendamaMapPointGroup => {
  if (group === 'club' || group === 'brand') {
    return group;
  }
  return 'practice';
};

const normalizeMapPointLogo = (logo: unknown): KendamaMapPointLogo | undefined => {
  if (!logo || typeof logo !== 'object') {
    return undefined;
  }

  const imageUrl = typeof (logo as KendamaMapPointLogo).imageUrl === 'string'
    ? (logo as KendamaMapPointLogo).imageUrl.trim()
    : '';
  if (!imageUrl) {
    return undefined;
  }

  return {
    imageUrl,
    background: (logo as KendamaMapPointLogo).background === 'white' ? 'white' : 'black',
  };
};

const normalizeMapPoint = (point: Partial<KendamaMapPoint>, index: number): KendamaMapPoint | null => {
  const fallbackId = `kendama-point-${Date.now()}-${index + 1}`;
  const id = String(point.id || fallbackId).trim() || fallbackId;
  const name = String(point.name || '').trim();
  const lng = Number(point.lng);
  const lat = Number(point.lat);

  if (!name || !Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }

  return {
    id,
    name,
    lng,
    lat,
    description: typeof point.description === 'string' ? point.description.trim() : '',
    group: normalizeMapPointGroup(point.group),
    logo: normalizeMapPointLogo(point.logo),
    updatedAt: typeof point.updatedAt === 'number' && Number.isFinite(point.updatedAt) ? point.updatedAt : Date.now(),
  };
};

const isJsonResponse = (response: Response) => {
  const contentType = response.headers.get('content-type') || '';
  return contentType.toLowerCase().includes('application/json');
};

const sanitizeKendamaMapPointsWithFallback = (points: unknown, fallbackToDefaults: boolean): KendamaMapPoint[] => {
  if (!Array.isArray(points)) {
    return fallbackToDefaults ? defaultKendamaMapPoints.map((point) => ({ ...point })) : [];
  }

  // Use Map for O(n) dedup instead of findIndex O(n²)
  const seen = new Map<string, KendamaMapPoint>();
  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    if (!point || typeof point !== 'object') continue;
    const normalized = normalizeMapPoint(point as Partial<KendamaMapPoint>, i);
    if (normalized && !seen.has(normalized.id)) {
      seen.set(normalized.id, normalized);
    }
  }
  const normalized = Array.from(seen.values());

  if (normalized.length > 0) {
    return normalized;
  }

  return fallbackToDefaults ? defaultKendamaMapPoints.map((point) => ({ ...point })) : [];
};

export const sanitizeKendamaMapPoints = (points: unknown): KendamaMapPoint[] =>
  sanitizeKendamaMapPointsWithFallback(points, true);

const sanitizePersistedKendamaMapPoints = (points: unknown): KendamaMapPoint[] =>
  sanitizeKendamaMapPointsWithFallback(points, false);

const clonePoints = (points: KendamaMapPoint[]) =>
  points.map((point) => ({
    ...point,
    logo: point.logo ? { ...point.logo } : undefined,
  }));

const createCompactKendamaMapCache = (points: KendamaMapPoint[]) =>
  points.map((point) => {
    if (!point.logo?.imageUrl?.startsWith('data:image/')) {
      return {
        ...point,
        logo: point.logo ? { ...point.logo } : undefined,
      };
    }

    return {
      ...point,
      // 本地缓存空间不足时，移除 base64 logo，仅保留点位主数据
      logo: undefined,
    };
  });

const readLocalKendamaMapPoints = (): KendamaMapPoint[] => {
  if (typeof window === 'undefined') {
    return defaultKendamaMapPoints.map((point) => ({ ...point }));
  }

  try {
    const localData = window.localStorage.getItem(KENDAMA_MAP_LOCAL_STORAGE_KEY);
    if (!localData) {
      return [];
    }
    return sanitizePersistedKendamaMapPoints(JSON.parse(localData));
  } catch (error) {
    console.error('Read local kendama map points error:', error);
    return [];
  }
};

const writeLocalKendamaMapPoints = (points: KendamaMapPoint[]) => {
  if (typeof window === 'undefined') return;
  const doWrite = () => {
    const serialized = JSON.stringify(points);
    try {
      window.localStorage.setItem(KENDAMA_MAP_LOCAL_STORAGE_KEY, serialized);
    } catch (error) {
      const compactPoints = createCompactKendamaMapCache(points);
      const compactSerialized = JSON.stringify(compactPoints);
      try {
        window.localStorage.setItem(KENDAMA_MAP_LOCAL_STORAGE_KEY, compactSerialized);
      } catch (compactError) {
        try {
          window.localStorage.removeItem(KENDAMA_MAP_LOCAL_STORAGE_KEY);
        } catch {}
      }
    }
  };
  (window as any).requestIdleCallback
    ? (window as any).requestIdleCallback(doWrite)
    : setTimeout(doWrite, 0);
};

const sortPointsForComparison = (points: KendamaMapPoint[]) =>
  [...points].sort((left, right) => {
    if (left.id !== right.id) return left.id.localeCompare(right.id);
    return (left.updatedAt ?? 0) - (right.updatedAt ?? 0);
  });

const areSamePoints = (left: KendamaMapPoint[], right: KendamaMapPoint[]) =>
  JSON.stringify(sortPointsForComparison(left)) === JSON.stringify(sortPointsForComparison(right));

const mergeKendamaMapPoints = (primaryPoints: KendamaMapPoint[], secondaryPoints: KendamaMapPoint[]) => {
  const mergedById = new Map<string, KendamaMapPoint>();

  const applyPoint = (point: KendamaMapPoint) => {
    const current = mergedById.get(point.id);
    if (!current || (point.updatedAt ?? 0) >= (current.updatedAt ?? 0)) {
      mergedById.set(point.id, { ...point });
    }
  };

  primaryPoints.forEach(applyPoint);
  secondaryPoints.forEach(applyPoint);

  return sanitizePersistedKendamaMapPoints(Array.from(mergedById.values()));
};

const trySyncMergedPointsToServer = async (points: KendamaMapPoint[]) => {
  try {
    const response = await fetch(getMapSaveEndpoint(), {
      method: 'POST',
      headers: phpAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ points }),
    });

    if (!response.ok || !isJsonResponse(response)) {
      return null;
    }

    const data = await response.json();
    return sanitizePersistedKendamaMapPoints(data.points);
  } catch {
    return null;
  }
};

export const fetchKendamaMapPoints = async (signal?: AbortSignal, forceRefresh = false): Promise<KendamaMapPoint[]> => {
  if (typeof window === 'undefined') {
    return defaultKendamaMapPoints.map((point) => ({ ...point }));
  }

  // 构建带缓存破坏的 GET URL
  const buildUrl = (base: string) =>
    `${base}${base.includes('?') ? '&' : '?'}_t=${Date.now()}`;

  const doFetch = async (url: string, opts: RequestInit): Promise<Response> =>
    fetch(url, { ...opts, signal });

  try {
    let response: Response;

    if (forceRefresh) {
      const fetchUrl = buildUrl(getMapFetchEndpoint());

      // 策略 1：带 token 的 GET + cache:reload
      try {
        response = await doFetch(fetchUrl, {
          headers: phpAuthHeaders(),
          cache: 'reload',
        });
      } catch {
        // 策略 2：不带鉴权头的裸请求（绕过某些代理/ad-blocker 拦截导致的异常）
        response = await doFetch(buildUrl(getMapFetchEndpoint()), {
          cache: 'reload',
        });
      }
    } else {
      // 正常加载：GET + cache:no-store
      response = await doFetch(getMapFetchEndpoint(), {
        headers: phpAuthHeaders(),
        cache: 'no-store',
      });
    }

    if (response.ok && isJsonResponse(response)) {
      const data = await response.json();
      const serverPoints = sanitizePersistedKendamaMapPoints(data.points);
      writeLocalKendamaMapPoints(serverPoints);
      return serverPoints;
    }

    // forceRefresh 模式下，非 JSON 响应也直接抛错
    if (forceRefresh) {
      if (response.ok) {
        throw new Error('服务器返回了非预期格式的数据，请稍后重试。');
      }
      throw new Error(`服务器返回错误 (${response.status})，请检查网络后重试。`);
    }

    if (response.ok) {
      console.warn('Kendama map API returned a non-JSON response, falling back to local data.');
    }
  } catch (error) {
    if ((error as Error)?.name === 'AbortError' || signal?.aborted) {
      throw error;
    }
    if (forceRefresh) {
      throw error instanceof Error ? error : new Error('刷新点位失败，请检查网络连接。');
    }
    console.error('Fetch kendama map points error:', error);
  }

  // 仅在非强制刷新时，回退到本地缓存 / 默认数据
  const localPoints = readLocalKendamaMapPoints();
  if (localPoints.length > 0) {
    return clonePoints(localPoints);
  }

  return defaultKendamaMapPoints.map((point) => ({ ...point }));
};

export const saveKendamaMapPoints = async (points: KendamaMapPoint[]): Promise<KendamaMapPoint[]> => {
  const sanitizedPoints = sanitizePersistedKendamaMapPoints(points);

  if (typeof window === 'undefined') {
    return sanitizedPoints;
  }

  writeLocalKendamaMapPoints(sanitizedPoints);

  try {
    const response = await fetch(getMapSaveEndpoint(), {
      method: 'POST',
      headers: phpAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ points: sanitizedPoints }),
    });

    if (!response.ok) {
      const data = isJsonResponse(response) ? await response.json().catch(() => ({})) : {};
      throw new Error(String(data?.error || '后端保存地图点位失败，当前设备已保留一份本地备份。'));
    }

    if (isJsonResponse(response)) {
      const data = await response.json();
      const persistedPoints = sanitizePersistedKendamaMapPoints(data.points);
      writeLocalKendamaMapPoints(persistedPoints);
      return persistedPoints;
    }

    console.warn('Kendama map save API returned a non-JSON response, falling back to local storage.');
  } catch (error) {
    console.error('Save kendama map points error:', error);
    throw error instanceof Error ? error : new Error('后端保存地图点位失败，当前设备已保留一份本地备份。');
  }

  return sanitizedPoints;
};

export const submitKendamaMapSuggestion = async (payload: {
  address: string;
  name: string;
  contact?: string;
  group: KendamaMapPointGroup;
}): Promise<{ message?: string }> => {
  const response = await fetch(getMapSuggestionSubmitEndpoint(), {
    method: 'POST',
    headers: phpAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      address: payload.address.trim(),
      name: payload.name.trim(),
      contact: typeof payload.contact === 'string' ? payload.contact.trim() : '',
      group: payload.group,
    }),
  });

  if (!response.ok) {
    const data = isJsonResponse(response) ? await response.json().catch(() => ({})) : {};
    throw new Error(String(data?.error || '提交标点申请失败，请稍后重试。'));
  }

  if (!isJsonResponse(response)) {
    return { message: '标点申请已提交。' };
  }

  const data = await response.json();
  return { message: typeof data?.message === 'string' ? data.message : '标点申请已提交。' };
};
